import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { CreditCard, Loader2, Upload, Eye, Download, Users, Save, FileText, AlertTriangle, Search } from "lucide-react";
import { OpCardsToolbar } from "./OpCardsToolbar";
import { CardRiderActions, RiderChip } from "./CardRiderActions";
import { NoCardRiders, CardKindToggle } from "./NoCardRiders";
import { api, fileUrl, shrinkImage } from "../lib/api";
import { Input } from "../components/ui/input";
import { InAppFilePreview } from "./InAppFilePreview";

// Central operating-card manager: edit a card's expiry / inspection / file ONCE and it reflects
// to every vehicle sharing that card number.
export const tierOf = (n) => (n >= 3 ? 3 : n);
export const TIER = {
  3: { label: "3 مناديب فأكثر", row: "border-rose-300 bg-rose-50/40", icon: "bg-rose-100 text-rose-700", tile: "border-rose-200 bg-rose-50 text-rose-700", on: "ring-2 ring-rose-400" },
  2: { label: "2 مندوب", row: "border-amber-300 bg-amber-50/40", icon: "bg-amber-100 text-amber-700", tile: "border-amber-200 bg-amber-50 text-amber-700", on: "ring-2 ring-amber-400" },
  1: { label: "1 مندوب", row: "border-emerald-300 bg-emerald-50/40", icon: "bg-emerald-100 text-emerald-700", tile: "border-emerald-200 bg-emerald-50 text-emerald-700", on: "ring-2 ring-emerald-400" },
  0: { label: "بدون مندوب", row: "border-slate-300 bg-slate-50", icon: "bg-slate-200 text-slate-500", tile: "border-slate-200 bg-slate-100 text-slate-600", on: "ring-2 ring-slate-400" },
};

const CapacityBox = ({ kind, title, cards, max }) => {
  const cap = cards.length * max;
  const filled = cards.reduce((n, c) => n + Math.min(c.riders_count, max), 0);
  const left = cap - filled;
  const pct = cap ? Math.round((filled / cap) * 100) : 0;
  return (
    <div data-testid={`opcards-capacity-${kind}`} className={`rounded-xl border p-3 space-y-2 ${kind === "car" ? "border-violet-200 bg-violet-50" : "border-sky-200 bg-sky-50"}`}>
      <p className="text-xs font-bold text-slate-700">{title}</p>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div><div data-testid={`opcards-total-${kind}`} className="font-heading text-2xl font-bold font-mono-nums text-slate-800">{cards.length}</div><div className="text-[11px] text-slate-600">إجمالي الكروت</div></div>
        <div><div data-testid={`opcards-filled-${kind}`} className={`font-heading text-2xl font-bold font-mono-nums ${kind === "car" ? "text-violet-700" : "text-sky-700"}`}>{filled}<span className="text-sm text-slate-400"> / {cap}</span></div><div className="text-[11px] text-slate-600">المناديب المسجّلون</div></div>
        <div><div data-testid={`opcards-remaining-${kind}`} className={`font-heading text-2xl font-bold font-mono-nums ${left ? "text-rose-600" : "text-emerald-600"}`}>{left}</div><div className="text-[11px] text-slate-600">المتبقي للاكتمال</div></div>
      </div>
      <div className="h-2 rounded-full bg-white overflow-hidden"><div className={`h-full rounded-full transition-[width] duration-700 ${kind === "car" ? "bg-violet-500" : "bg-sky-500"}`} style={{ width: `${pct}%` }} /></div>
      <p className="text-[11px] text-slate-500 text-center">
        {!cards.length ? "لا توجد كروت" : left ? `تحتاج ${left} مندوب لاكتمال ${cap} (${max === 1 ? "مندوب واحد" : `${max} مناديب`} على كل كرت) — ${pct}%` : `كل الكروت مكتملة (${max === 1 ? "مندوب واحد" : `${max} مناديب`} على كل كرت)`}
      </p>
    </div>
  );
};

const CardRow = ({ card, cards = [], onSaved, maxRiders, employees, riderCards = {} }) => {
  const [exp, setExp] = useState(card.operating_card_expiry || "");
  const [insp, setInsp] = useState(card.inspection_expiry || "");
  const [fileId, setFileId] = useState(card.operating_card_file_id || null);
  const [plate, setPlate] = useState(card.plate_number || "");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [regId, setRegId] = useState(card.registration_file_id || null);
  const [regExp, setRegExp] = useState(card.registration_expiry || "");
  const [regUp, setRegUp] = useState(false);
  const [pv, setPv] = useState(null);

  const onRegUpload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setRegUp(true);
    try {
      try { await f.slice(0, 16).arrayBuffer(); } catch {
        toast.error("تعذّر قراءة الملف من الجوال — احفظه في المعرض أولاً أو صوّر الاستمارة بالكاميرا ثم ارفعها");
        return;
      }
      const up = await shrinkImage(f);
      let r;
      try { r = await api.uploadExtract(up, "استمارة"); }
      catch (e1) { if (e1?.response) throw e1; await new Promise((ok) => setTimeout(ok, 1500)); r = await api.uploadExtract(up, "استمارة"); }
      setRegId(r.attachment_id);
      if (r.expiry_date) setRegExp(r.expiry_date);
      if (r.plate_number && !plate) setPlate(r.plate_number);
      toast.success(`تم رفع الاستمارة${r.expiry_date ? ` — تنتهي ${r.expiry_date}` : ""}${r.plate_number ? ` · لوحة ${r.plate_number}` : ""} — اضغط حفظ`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(`تعذّر رفع الاستمارة${typeof d === "string" ? ` — ${d}` : err?.response?.status ? ` (رمز ${err.response.status})` : " — تحقق من الاتصال"}`);
    }
    finally { setRegUp(false); }
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const r = await api.uploadExtract(f, "كرت التشغيل");
      setFileId(r.attachment_id);
      if (r.plate_number) setPlate(r.plate_number);
      if (r.expiry_date) setExp(r.expiry_date);
      toast.success(`تم رفع ملف الكرت${r.plate_number ? ` وقراءة اللوحة ${r.plate_number}` : ""} — اضغط حفظ لتطبيقه`);
    } catch { toast.error("تعذّر رفع الملف"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.applyOperatingCard({ number: card.number, operating_card_expiry: exp, inspection_expiry: insp, operating_card_file_id: fileId, plate_number: plate, registration_file_id: regId || "", registration_expiry: regExp });
      toast.success(`تم تطبيق الكرت على ${r.updated} مركبة${r.employees_updated ? ` وتحديث مستند ${r.employees_updated} مندوب` : ""}${r.plate_linked ? ` وربط ${r.plate_linked} مركبة باللوحة` : ""}`);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); }
    finally { setBusy(false); }
  };

  return (
    <div className={`rounded-xl border p-3 space-y-3 transition-colors ${TIER[tierOf(card.riders_count)].row}`} data-testid={`opcard-${card.number}`} data-tier={tierOf(card.riders_count)}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className={`grid h-9 w-9 place-items-center rounded-lg ${TIER[tierOf(card.riders_count)].icon}`}><CreditCard className="h-5 w-5" /></span>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-slate-800 font-mono-nums" dir="ltr">{card.number}</span>
              <Input data-testid={`opcard-plate-${card.number}`} value={plate} onChange={(e) => setPlate(e.target.value)}
                placeholder="لوحة الكرت" title="رقم اللوحة المكتوب على الكرت" className="h-7 w-32 text-xs font-mono-nums" />
            </div>
            <div className="text-[11px] text-slate-400 flex items-center gap-2"><span className="flex items-center gap-1"><Users className="h-3 w-3" /> {card.count} مركبة</span>
              <CardKindToggle card={card} onChanged={onSaved} /></div>
          </div>
        </div>
        <span data-testid={`opcard-riders-count-${card.number}`}
          className={`rounded-full text-[11px] font-semibold px-2 py-0.5 ${card.over_limit ? "bg-rose-600 text-white" : TIER[tierOf(card.riders_count)].icon}`}>
          {card.riders_count} مندوب{card.over_limit ? ` — تجاوز الحدّ (${card.max_riders || maxRiders})` : ""}
        </span>
        {card.declared_riders != null && card.declared_riders !== card.riders_count && (
          <span data-testid={`opcard-declared-${card.number}`} className="rounded-full bg-sky-50 border border-sky-200 text-sky-700 text-[11px] px-2 py-0.5">حسب الملف: {card.declared_riders}</span>
        )}
      </div>
      {card.over_limit && (
        <div data-testid={`opcard-over-limit-${card.number}`} className="flex items-center gap-1.5 rounded-lg bg-rose-50 border border-rose-200 px-2.5 py-1.5 text-xs text-rose-700">
          <AlertTriangle className="h-3.5 w-3.5" /> هذا الكرت مسجّل على {card.riders_count} مناديب — الحدّ المسموح {card.max_riders || maxRiders}{card.vehicle_kind === "car" ? " (سيارة)" : ""}
        </div>
      )}
      {card.riders?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {card.riders.map((r) => <RiderChip key={r.id} rider={r} card={card} cards={cards} maxRiders={maxRiders} onRemoved={onSaved}
            dupCards={(riderCards[r.id] || []).filter((n) => n !== card.number)} />)}
        </div>
      )}
      <CardRiderActions card={card} employees={employees} riderCards={riderCards} onChanged={onSaved} />

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">انتهاء كرت التشغيل</Label>
          <Input type="date" dir="ltr" data-testid={`opcard-exp-${card.number}`} value={exp} onChange={(e) => setExp(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">انتهاء الفحص الدوري</Label>
          <Input type="date" dir="ltr" data-testid={`opcard-insp-${card.number}`} value={insp} onChange={(e) => setInsp(e.target.value)} />
        </div>
      </div>

      <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-2.5 space-y-2" data-testid={`opcard-reg-${card.number}`}>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-xs font-bold text-indigo-800">الاستمارة <span className="font-normal text-indigo-600">— تنتقل مع الكرت لكل مندوب عليه</span></span>
          <div className="flex items-center gap-2">
            <input type="file" accept="image/*,application/pdf" hidden id={`opcard-reg-file-${card.number}`} data-testid={`opcard-reg-file-${card.number}`} onChange={onRegUpload} />
            <Button type="button" size="sm" variant="outline" data-testid={`opcard-reg-upload-${card.number}`} className="gap-1.5 h-8 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              disabled={regUp} onClick={() => document.getElementById(`opcard-reg-file-${card.number}`)?.click()}>
              {regUp ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {regId ? "استبدال الاستمارة" : "رفع ملف الاستمارة"}
            </Button>
            {regId && <>
              <button type="button" onClick={() => setPv({ id: regId, name: `استمارة ${plate || card.number}` })} data-testid={`opcard-reg-view-${card.number}`} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-indigo-600"><Eye className="h-3.5 w-3.5" /> معاينة</button>
              <a href={`${fileUrl(regId)}?download=1`} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-indigo-600"><Download className="h-3.5 w-3.5" /> تنزيل</a>
            </>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs shrink-0">انتهاء الاستمارة</Label>
          <Input type="date" dir="ltr" className="h-8" data-testid={`opcard-reg-exp-${card.number}`} value={regExp} onChange={(e) => setRegExp(e.target.value)} />
        </div>
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <input type="file" accept="image/*,application/pdf" hidden id={`opcard-file-${card.number}`} data-testid={`opcard-file-${card.number}`} onChange={onUpload} />
          <Button type="button" size="sm" variant="outline" className="gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50"
            disabled={uploading} onClick={() => document.getElementById(`opcard-file-${card.number}`)?.click()}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {fileId ? "استبدال الملف" : "رفع ملف الكرت"}
          </Button>
          {fileId && (
            <>
              <button type="button" onClick={() => setPv({ id: fileId, name: `كرت التشغيل \u2066${card.number}\u2069` })} data-testid={`opcard-view-${card.number}`} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-blue-600"><Eye className="h-3.5 w-3.5" /> معاينة</button>
              <a href={`${fileUrl(fileId)}?download=1`} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-blue-600"><Download className="h-3.5 w-3.5" /> تنزيل</a>
            </>
          )}
          {fileId && <FileText className="h-4 w-4 text-emerald-600" />}
        </div>
        <Button type="button" size="sm" data-testid={`opcard-save-${card.number}`} disabled={busy} onClick={save} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} حفظ
        </Button>
      </div>
      <InAppFilePreview file={pv} onClose={() => setPv(null)} />
    </div>
  );
};

export const OperatingCardsDialog = ({ open, onOpenChange, onSaved }) => {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState("all");
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.listOperatingCards().then((r) => setData(r)).catch(() => setData({ cards: [], max_riders: 3, over_limit_count: 0 }));
  useEffect(() => {
    if (!open) return;
    setData(null); setSelected("all"); setQ(""); load();
    api.listEmployees().then((r) => setEmployees((Array.isArray(r) ? r : r.items || []).map((e) => ({ id: e.id, name: e.name, branch: e.branch, vehicle_type: e.vehicle_type || "", nationality: e.nationality || "", status: e.employee_status || e.tashark_status || "" })))).catch(() => {});
  }, [open]);
  const cards = data?.cards || [];
  const riderCards = {};
  cards.forEach((c) => (c.riders || []).forEach((r) => { (riderCards[r.id] = riderCards[r.id] || []).push(c.number); }));
  const dupIds = Object.keys(riderCards).filter((id) => riderCards[id].length > 1);
  const norm = (t) => String(t || "").toLowerCase().replace(/[أإآ]/g, "ا").replace(/ى/g, "ي").replace(/ة/g, "ه").replace(/[\s.\-]+/g, "");
  const nq = norm(q);
  const shown = (selected === "all" ? cards : selected === "over" ? cards.filter((c) => c.over_limit)
    : selected === "dup" ? cards.filter((c) => (c.riders || []).some((r) => dupIds.includes(r.id)))
    : selected === "noreg" ? cards.filter((c) => !c.registration_file_id)
    : selected.startsWith("tier:") ? cards.filter((c) => tierOf(c.riders_count) === Number(selected.slice(5))) : cards.filter((c) => c.number === selected))
    .filter((c) => !nq || norm([c.number, c.plate_number, ...(c.riders || []).map((r) => r.name), ...(c.vehicles || []).map((v) => v.identifier)].join(" ")).includes(nq));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="sm:max-w-2xl font-body max-h-[88vh] overflow-y-auto" data-testid="operating-cards-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-xl flex items-center gap-2"><CreditCard className="h-5 w-5 text-amber-600" /> ضبط كروت التشغيل</DialogTitle>
          <DialogDescription className="text-right">
            عدّل انتهاء الكرت/الفحص وارفع ملف الكرت مرة واحدة — يُطبَّق تلقائياً على كل المركبات والمناديب الذين يحملون نفس رقم الكرت.
          </DialogDescription>
        </DialogHeader>
        {data === null ? (
          <div className="py-16 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
        ) : (
          <>
            <OpCardsToolbar data={data} selected={selected} onSelect={setSelected} onUploaded={() => { load(); onSaved?.(); }} />
            {dupIds.length > 0 && (
              <button type="button" data-testid="opcards-dup-banner" onClick={() => setSelected(selected === "dup" ? "all" : "dup")}
                className="w-full flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 text-right hover:bg-rose-100 transition-colors">
                <AlertTriangle className="h-4 w-4 shrink-0" /> {dupIds.length} مندوب مكرر على أكثر من كرت — اضغط للعرض
              </button>
            )}
            {cards.length > 0 && (() => {
              const n = cards.filter((c) => !c.registration_file_id).length;
              const on = selected === "noreg";
              return (
                <button type="button" data-testid="opcards-noreg-filter" onClick={() => setSelected(on ? "all" : "noreg")}
                  className={`w-full flex items-center justify-between gap-2 rounded-xl border px-3 py-2 text-sm text-right transition-colors ${on ? "border-indigo-400 bg-indigo-100 text-indigo-800" : "border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"}`}>
                  <span className="flex items-center gap-2"><FileText className="h-4 w-4 shrink-0" /> كروت بدون استمارة {on ? "— اضغط لعرض الكل" : ""}</span>
                  <span data-testid="opcards-noreg-count" className="font-mono-nums font-bold">{n}</span>
                </button>
              );
            })()}
            {cards.length > 0 && (
              <div className="grid gap-2 sm:grid-cols-2">
                <CapacityBox kind="moto" title="كروت الدراجات النارية" cards={cards.filter((c) => c.vehicle_kind !== "car")} max={data.max_riders || 3} />
                <CapacityBox kind="car" title="كروت السيارات" cards={cards.filter((c) => c.vehicle_kind === "car")} max={data.max_riders_car || 1} />
              </div>
            )}
            <NoCardRiders employees={employees} riderCards={riderCards} cards={cards} onChanged={() => { load(); onSaved?.(); }} />
            {cards.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="opcards-tiers">
                {[3, 2, 1, 0].map((t) => {
                  const n = cards.filter((c) => tierOf(c.riders_count) === t).length;
                  const on = selected === `tier:${t}`;
                  return (
                    <button key={t} type="button" data-testid={`opcards-tier-${t}`} onClick={() => setSelected(on ? "all" : `tier:${t}`)}
                      className={`rounded-xl border px-2 py-2.5 text-center transition-[box-shadow,transform] hover:-translate-y-0.5 ${TIER[t].tile} ${on ? TIER[t].on : ""}`}>
                      <div className="font-heading text-2xl font-bold font-mono-nums leading-none">{n}</div>
                      <div className="mt-1 text-[11px] font-semibold">{n === 1 ? "كرت" : "كروت"} · {TIER[t].label}</div>
                    </button>
                  );
                })}
              </div>
            )}
            {cards.length > 0 && (
              <div className="relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input data-testid="opcards-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث برقم الكرت أو اللوحة أو اسم المندوب…" className="pr-9" />
                {q && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[11px] text-slate-400" data-testid="opcards-search-count">{shown.length} نتيجة</span>}
              </div>
            )}
            {cards.length === 0 ? (
              <div className="py-16 text-center text-slate-400" data-testid="opcards-empty">لا توجد كروت تشغيل مسجّلة بعد. ارفع ملفات الكروت دفعة واحدة أو أضف رقم الكرت في بيانات المركبة.</div>
            ) : (
              <div className="space-y-3">
                {shown.length === 0 && <p className="py-8 text-center text-sm text-slate-400" data-testid="opcards-no-results">لا يوجد كرت مطابق للبحث</p>}
                {shown.map((c) => <CardRow key={c.number} card={c} cards={cards} employees={employees} riderCards={riderCards} maxRiders={data.max_riders} onSaved={() => { load(); onSaved?.(); }} />)}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
